import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Search, Download, Check, Copy, Pencil, Trash2, ArrowDownCircle, ArrowUpCircle, Filter } from "lucide-react";
import api, { API } from "@/lib/api";
import { fmtEUR, fmtDate, MONTHS_FR } from "@/lib/format";
import { CARD, Label, Loader, EmptyState } from "@/components/common";
import TransactionDialog from "@/components/TransactionDialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const now = new Date();

export function TxRow({ t, onChange }) {
  const [editing, setEditing] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const income = t.type === "income";
  const done = t.status === "completed";
  const ecart = done && t.actual_amount != null ? t.actual_amount - t.amount : null;

  const complete = async () => { await api.post(`/transactions/${t.id}/complete`); toast.success("Marqué comme réalisé"); onChange(); };
  const duplicate = async () => { await api.post(`/transactions/${t.id}/duplicate`); toast.success("Dupliqué"); onChange(); };
  const del = async () => { await api.delete(`/transactions/${t.id}`); toast.success("Supprimé"); setConfirm(false); onChange(); };

  return (
    <div className="flex items-center gap-3 border-b border-gray-50 py-3 last:border-0" data-testid={`tx-row-${t.id}`}>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${income ? "bg-emerald-50 text-emerald-600" : "bg-rose-50 text-rose-600"}`}>
        {income ? <ArrowDownCircle className="h-5 w-5" /> : <ArrowUpCircle className="h-5 w-5" />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{t.label}</p>
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span>{fmtDate(t.transaction_date)}</span>
          <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${done ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
            {done ? (income ? "Encaissé" : "Payé") : "Prévu"}
          </span>
          {ecart != null && Math.abs(ecart) > 0.01 && (
            <span className={`font-semibold ${ecart > 0 ? "text-amber-600" : "text-emerald-600"}`}>
              écart {ecart > 0 ? "+" : ""}{fmtEUR(ecart)}
            </span>
          )}
        </div>
      </div>
      <span className={`font-display text-sm font-bold ${income ? "text-emerald-600" : "text-gray-800"}`}>
        {income ? "+" : "−"}{fmtEUR(done && t.actual_amount != null ? t.actual_amount : t.amount)}
      </span>
      <div className="flex items-center gap-0.5">
        {!done && <button title="Réalisé" onClick={complete} data-testid={`complete-${t.id}`} className="rounded-lg p-1.5 text-gray-400 hover:bg-emerald-50 hover:text-emerald-600"><Check className="h-4 w-4" /></button>}
        <button title="Modifier" onClick={() => setEditing(true)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"><Pencil className="h-4 w-4" /></button>
        <button title="Dupliquer" onClick={duplicate} className="hidden rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 sm:block"><Copy className="h-4 w-4" /></button>
        <button title="Supprimer" onClick={() => setConfirm(true)} data-testid={`delete-${t.id}`} className="rounded-lg p-1.5 text-gray-400 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
      </div>
      <TransactionDialog open={editing} onOpenChange={setEditing} type={t.type} editing={t} onSaved={onChange} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce mouvement ?</AlertDialogTitle>
            <AlertDialogDescription>« {t.label} » sera définitivement supprimé.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="confirm-delete" className="rounded-full bg-rose-600 hover:bg-rose-700">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Mouvements() {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [ym, setYm] = useState({ year: now.getFullYear(), month: now.getMonth() + 1 });

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ year: ym.year, month: ym.month });
    if (type !== "all") params.set("type", type);
    if (status !== "all") params.set("status", status);
    if (q) params.set("q", q);
    try {
      const { data } = await api.get(`/transactions?${params.toString()}`);
      setTxns(data);
    } finally { setLoading(false); }
  }, [ym, type, status, q]);

  useEffect(() => { const t = setTimeout(load, q ? 350 : 0); return () => clearTimeout(t); }, [load, q]);

  const exportUrl = (fmt) => `${API}/export?format=${fmt}`;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-400">Relevé du cabinet</p>
          <h1 className="font-display text-2xl font-bold sm:text-3xl">Mouvements</h1>
        </div>
        <div className="flex gap-2">
          <a href={exportUrl("csv")} data-testid="export-csv"><Button variant="outline" className="rounded-full"><Download className="mr-1.5 h-4 w-4" />CSV</Button></a>
          <a href={exportUrl("xlsx")} data-testid="export-xlsx"><Button variant="outline" className="rounded-full"><Download className="mr-1.5 h-4 w-4" />Excel</Button></a>
        </div>
      </div>

      <div className={`${CARD} mt-5 p-4`}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (prothèse, loyer, Dr Charles…)" className="rounded-xl pl-9" />
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <Select value={`${ym.year}-${ym.month}`} onValueChange={(v) => { const [y, m] = v.split("-"); setYm({ year: +y, month: +m }); }}>
            <SelectTrigger className="w-40 rounded-full"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Array.from({ length: 15 }).map((_, i) => {
                const d = new Date(now.getFullYear(), now.getMonth() - 3 + i, 1);
                return <SelectItem key={i} value={`${d.getFullYear()}-${d.getMonth() + 1}`}>{MONTHS_FR[d.getMonth() + 1]} {d.getFullYear()}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger className="w-32 rounded-full" data-testid="filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tout</SelectItem>
              <SelectItem value="income">Recettes</SelectItem>
              <SelectItem value="expense">Dépenses</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-32 rounded-full" data-testid="filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="planned">Prévu</SelectItem>
              <SelectItem value="completed">Réalisé</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className={`${CARD} mt-4 p-4 sm:p-6`}>
        {loading ? <Loader /> : txns.length === 0 ? (
          <EmptyState title="Aucun mouvement" subtitle="Aucune opération ne correspond à ces filtres." />
        ) : (
          txns.map((t) => <TxRow key={t.id} t={t} onChange={load} />)
        )}
      </div>
    </div>
  );
}
